import { InspectionResultsApi } from '../../api-generated/apis';
/**
 * Machine detail page with tabbed content.
 *
 * Displays a header with machine name, type, StatusBadge, and Edit button.
 * Three tabs:
 *   - Parts: FilterableDataTable of machine parts
 *   - Recent Results: Timeline of recent inspection results
 *   - Health Trends: Sparkline charts per part using recharts
 *
 * Edit button opens a DrawerPanel with MachineDrawerForm (mode='edit').
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import { GridColDef } from '@mui/x-data-grid';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  FilterableDataTable,
  DrawerPanel,
  StatusBadge,
  PageContainer,
  type HealthStatus,
  type DrawerAction,
} from '../../components/shared';
import MachineDrawerForm from './MachineDrawerForm';
import { MachineControllerApi } from '../../api-generated/apis';
import type { Machine, MachinePart } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

const machineApi = new MachineControllerApi(apiConfig);

// ─── Types ───────────────────────────────────────────────────────────────────

interface MachinePartRow {
  id: number;
  name: string;
  partNumber: string;
  category: string;
  status: HealthStatus;
}

interface ResultItem {
  id: number;
  completedAt: string;
  templateName: string;
  overallStatus: HealthStatus;
  passedCount: number;
  totalCount: number;
}

interface ResultApiItem {
  id: number;
  completedAt?: string;
  formInstance?: {
    id?: number;
    checklistTemplate?: { name?: string };
  };
  answers?: Array<{ statusValue?: string }>;
}

interface TrendDataPoint {
  day: string;
  value: number;
}

// ─── API Fetcher for results (no generated API for paginated results) ────────

async function fetchRecentResults(machineId: string): Promise<{ content: ResultApiItem[] }> {
  const resultsApi = new InspectionResultsApi(apiConfig);
  return resultsApi.queryResults({
    machineId,
    page: 0,
    size: 10,
    sort: 'completedAt,desc',
  } as any) as any;
}

// ─── Mock Data Helpers ─────────────────────────────────────────────────

function mockPartCategory(partId: number): string {
  const categories = ['Mechanical', 'Electrical', 'Hydraulic', 'Pneumatic'];
  return categories[partId % categories.length];
}

/**
 * Generates 7 mock data points for a sparkline chart.
 * Uses a deterministic seed based on part id for consistent rendering.
 */
function generateMockTrendData(partId: number): TrendDataPoint[] {
  const points: TrendDataPoint[] = [];
  for (let i = 0; i < 7; i++) {
    const seed = (partId * 7 + i) % 100;
    points.push({
      day: `Day ${i + 1}`,
      value: 60 + (seed % 35),
    });
  }
  return points;
}

// ─── Column Definitions for Parts Tab ────────────────────────────────────────

const partsColumns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'partNumber', headerName: 'Part Number', flex: 0.8, minWidth: 120 },
  { field: 'category', headerName: 'Category', flex: 0.7, minWidth: 120 },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.6,
    minWidth: 130,
    renderCell: (params) => <StatusBadge status={params.value as HealthStatus} />,
  },
];

// ─── Tab Panel ───────────────────────────────────────────────────────────────

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box
    role="tabpanel"
    hidden={value !== index}
    id={`machine-tabpanel-${index}`}
    aria-labelledby={`machine-tab-${index}`}
    sx={{ flexGrow: 1, display: value === index ? 'flex' : 'none', flexDirection: 'column' }}
  >
    {value === index && children}
  </Box>
);

// ─── Component ───────────────────────────────────────────────────────────────

const MachineDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tabIndex, setTabIndex] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [partDrawerOpen, setPartDrawerOpen] = useState(false);
  const [partName, setPartName] = useState('');
  const [partNumber, setPartNumber] = useState('');
  const queryClient = useQueryClient();

  // Parts tab pagination
  const [partsPage, setPartsPage] = useState(0);
  const [partsPageSize, setPartsPageSize] = useState(25);

  // ─── Data fetching ─────────────────────────────────────────────────────────

  const machineQuery = useQuery<Machine, Error>({
    queryKey: ['machine', 'detail', id],
    queryFn: () => machineApi.getMachine({ id: id! }),
    enabled: !!id,
  });

  const partsQuery = useQuery<Array<MachinePart>, Error>({
    queryKey: ['machine-parts', id],
    queryFn: () => machineApi.getAllMachinePartsLegacy({ id: id! }),
    enabled: !!id,
  });

  const resultsQuery = useQuery<{ content: ResultApiItem[] }, Error>({
    queryKey: ['machine-results', id],
    queryFn: () => fetchRecentResults(id!),
    enabled: !!id,
  });

  // ─── Derived data ──────────────────────────────────────────────────────────

  const machine = machineQuery.data;

  const partsRows: MachinePartRow[] = useMemo(() => {
    const content: MachinePart[] = partsQuery.data ?? [];
    return content.map((part) => {
      const numericId = Number(part.id) || 0;
      return {
        id: numericId,
        name: part.name ?? 'Unnamed Part',
        partNumber: part.partNumber ?? '—',
        category: mockPartCategory(numericId),
        status: 'UNKNOWN' as HealthStatus,
      };
    });
  }, [partsQuery.data]);

  const recentResults: ResultItem[] = useMemo(() => {
    const content: ResultApiItem[] = resultsQuery.data?.content ?? [];
    return content.map((result: ResultApiItem) => {
      const answers = result.answers ?? [];
      const passedCount = answers.filter((a: { statusValue?: string }) => a.statusValue === 'PASS').length;
      return {
        id: result.id,
        completedAt: result.completedAt ?? '',
        templateName: result.formInstance?.checklistTemplate?.name ?? 'Unknown Template',
        overallStatus: 'UNKNOWN' as HealthStatus,
        passedCount,
        totalCount: answers.length || 1,
      };
    });
  }, [resultsQuery.data]);

  /** Health status — show UNKNOWN until real health data exists */
  const machineHealthStatus: HealthStatus = 'UNKNOWN';

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  }, []);

  const handleEditClick = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // ─── Add Part handlers ─────────────────────────────────────────────────────

  const addPartMutation = useMutation({
    mutationFn: (part: MachinePart) => machineApi.addPart({ id: id!, machinePart: part }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['machine-parts', id] });
      queryClient.invalidateQueries({ queryKey: ['machine', 'detail', id] });
      setPartDrawerOpen(false);
      setPartName('');
      setPartNumber('');
    },
  });

  const handleAddPart = useCallback(() => {
    setPartName('');
    setPartNumber('');
    setPartDrawerOpen(true);
  }, []);

  const handlePartSubmit = useCallback(() => {
    if (!partName.trim()) return;
    addPartMutation.mutate({
      name: partName.trim(),
      partNumber: partNumber.trim() || undefined,
    });
  }, [partName, partNumber, addPartMutation]);

  const partDrawerActions: DrawerAction[] = useMemo(() => [
    { label: 'Cancel', variant: 'outlined' as const, onClick: () => setPartDrawerOpen(false) },
    { label: 'Add Part', variant: 'contained' as const, onClick: handlePartSubmit, disabled: addPartMutation.isPending },
  ], [handlePartSubmit, addPartMutation.isPending]);

  // ─── Loading & Error States ────────────────────────────────────────────────

  if (machineQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (machineQuery.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Failed to load machine details. Please try again later.
        </Alert>
      </Box>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <PageContainer title="Machine Details">
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            {machine?.name ?? 'Machine'}
          </Typography>
          <Chip label={machine?.machineType?.name ?? 'Unknown'} size="small" variant="outlined" />
          <StatusBadge status={machineHealthStatus} />
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={handleEditClick}
          aria-label="Edit machine"
        >
          Edit
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="Machine detail tabs"
        >
          <Tab label="Parts" id="machine-tab-0" aria-controls="machine-tabpanel-0" />
          <Tab label="Recent Results" id="machine-tab-1" aria-controls="machine-tabpanel-1" />
          <Tab label="Health Trends" id="machine-tab-2" aria-controls="machine-tabpanel-2" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <TabPanel value={tabIndex} index={0}>
        <FilterableDataTable<MachinePartRow>
          rows={partsRows}
          columns={partsColumns}
          loading={partsQuery.isLoading}
          totalCount={partsRows.length}
          page={partsPage}
          pageSize={partsPageSize}
          onPageChange={setPartsPage}
          onPageSizeChange={setPartsPageSize}
          searchPlaceholder="Search parts…"
          primaryAction={{ label: 'Add Part', onClick: handleAddPart }}
        />
      </TabPanel>

      <TabPanel value={tabIndex} index={1}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
          {resultsQuery.isLoading && <CircularProgress size={24} />}
          {recentResults.length === 0 && !resultsQuery.isLoading && (
            <Typography variant="body2" color="text.secondary">
              No recent inspection results.
            </Typography>
          )}
          {recentResults.map((result) => (
            <Box
              key={result.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
                {result.completedAt
                  ? new Date(result.completedAt).toLocaleDateString()
                  : '—'}
              </Typography>
              <Typography variant="body1" sx={{ flex: 1 }}>
                {result.templateName}
              </Typography>
              <StatusBadge status={result.overallStatus} size="small" />
              <Chip
                label={`${result.passedCount}/${result.totalCount} passed`}
                size="small"
                variant="outlined"
              />
            </Box>
          ))}
        </Box>
      </TabPanel>

      <TabPanel value={tabIndex} index={2}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
          {partsQuery.isLoading && <CircularProgress size={24} />}
          {partsRows.length === 0 && !partsQuery.isLoading && (
            <Typography variant="body2" color="text.secondary">
              No parts available for health trends.
            </Typography>
          )}
          {partsRows.map((part) => {
            const data = generateMockTrendData(part.id);
            return (
              <Box key={part.id} sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {part.name}
                </Typography>
                <Box sx={{ width: '100%', height: 120 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke="#1976d2"
                        strokeWidth={2}
                        dot={false}
                        name={part.name}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            );
          })}
        </Box>
      </TabPanel>

      {/* Edit Drawer */}
      <MachineDrawerForm
        open={drawerOpen}
        onClose={handleDrawerClose}
        mode="edit"
        machineId={id ? Number(id) : undefined}
      />

      {/* Add Part Drawer */}
      <DrawerPanel
        open={partDrawerOpen}
        onClose={() => setPartDrawerOpen(false)}
        title="Add Part"
        width="narrow"
        actions={partDrawerActions}
        loading={addPartMutation.isPending}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Part Name"
            value={partName}
            onChange={(e) => setPartName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Part Number"
            value={partNumber}
            onChange={(e) => setPartNumber(e.target.value)}
            fullWidth
          />
        </Box>
      </DrawerPanel>
    </PageContainer>
  );
};

export default MachineDetailPage;
